import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import useLanguage from '@op/hooks/useLanguage';
import {
  getRealtimeSteamingAiDetail,
  getRealtimeSteamingDetail,
} from '@op/services';
import { useEffect, useMemo, useState } from 'react';
import { IRealtimeStreamingDetail } from '../type';
import { DBO_TM, DBO_U } from '@op/utils/format';
import moment from 'moment';
import { getLiveTimeFilter, getLiveTimeTitle } from '../utils';
import useRefresh from '@op/hooks/useRefresh';
import { useStateRef } from '@op/hooks/common';

export default function useStreamingDetail({
  id,
  gameInfo,
  setFilterTime,
  setTimeFilterValue,
  isEmpty,
  isGST,
}: {
  id: string;
  gameInfo: any;
  setFilterTime: (value: { start_time?: string; end_time?: string }) => void;
  setTimeFilterValue: (value: { startTime: string; endTime: string }) => void;
  isEmpty: boolean;
  isGST?: boolean;
}) {
  const [data, setData] = useState<IRealtimeStreamingDetail>({});
  const [timelineFilterRef, timelineFilter, setTimelineFilter] = useStateRef<{
    live_id: string;
  }>();
  const [hasLiveCharts, setHasLiveCharts] = useState(false);
  const [hasStreaming, setHasStreaming] = useState(false);
  const query = useMemo(() => {
    const data = DBO_U.queryObj(location.search);
    return {
      start_time: decodeURIComponent(data.start_time),
      end_time: decodeURIComponent(data.end_time),
    };
  }, []);

  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    defaultLoading: !isEmpty,
    clear: () => setData({}),
  });

  function request(defaultLoading?: boolean) {
    return wrapperCancelRequest(
      getRealtimeSteamingDetail,
      {
        live_id: id,
        should_include_timeline: true,
        feed_source: 'real_time_streaming',
        date_type: 'daily',
        ...gameInfo,
        ...query,
      },
      defaultLoading,
      { user_cache_expire: 5 },
    ).then(data => {
      setData(data || {});
      const hasLiveChart = isGST ? false : data?.has_live_chats_analysis;
      const hasStreaming = data?.has_streaming_analysis;
      setHasLiveCharts(hasLiveChart);
      setHasStreaming(hasStreaming);

      if (isGST || (!hasLiveChart && hasStreaming)) {
        // 这里设置不能超过23小时55分钟,因为过滤器无法设置超出这个时间的数据
        const { liveTime, filter } = getLiveTimeFilter(data);
        setFilterTime(filter);
        if (!isGST) {
          setTimeFilterValue(liveTime);
          initTimer();
        }
      }
    });
  }

  const {
    initTimer,
    date: time,
    addRefreshTask,
  } = useRefresh({
    key: 'real_time_streaming_refresh_time',
    defaultRefreshTask: { liveDetail: () => request(true) },
  });

  useEffect(() => {
    if (id && query.start_time && !isEmpty) {
      initTimer();
      request();
    }
  }, [id, isEmpty]);

  useEffect(() => {
    if (!data?.live_id) return;
    if (timelineFilterRef.current?.live_id === data.live_id) return;
    const [start_time, end_time] = DBO_TM.serverTime(
      [data.live_time!, data.live_time!],
      'daily',
    );
    setTimelineFilter({
      live_id: id,
      ...gameInfo,
      start_time,
      end_time,
      date_type: 'hourly',
    });
  }, [data]);

  return {
    data,
    loading,
    hasLiveCharts,
    hasStreaming,
    timelineFilter,
    time,
    addRefreshTask,
    initTimer,
  };
}

export function useStreamingDetailAutoAI({
  id,
  needRequest,
  addRefreshTask,
  liveTime,
  setHideContentAnalysis,
}: {
  id?: string;
  needRequest?: boolean;
  addRefreshTask: (key: string, task: () => Promise<void>) => void;
  liveTime: string;
  setHideContentAnalysis?: (value: boolean) => void;
}) {
  const { wrapperCancelRequest } = useCancelRequestHook({
    clear: () =>
      setData({ summary_en: '', summary_cn: '', start_time: '', end_time: '' }),
  });

  const [data, setData] = useState({
    summary_en: '',
    summary_cn: '',
    start_time: '',
    end_time: '',
  });

  function request(defaultLoading?: boolean) {
    return wrapperCancelRequest(
      getRealtimeSteamingAiDetail,
      { live_id: id },
      defaultLoading,
    ).then(data => {
      setData(data || {});
    });
  }

  useEffect(() => {
    if (id && needRequest) {
      request();
      addRefreshTask('real_time_streaming_ai_refresh_time', () =>
        request(true),
      );
    }
  }, [id, needRequest]);

  const { isEn } = useLanguage();

  const { content, time } = useMemo(() => {
    const { summary_cn, summary_en, start_time, end_time } = data || {};
    const aiContent = isEn ? summary_en : summary_cn;
    const startTime = moment(start_time).format('YYYY-MM-DD HH:mm:ss');
    const endTime = moment(end_time).format('YYYY-MM-DD HH:mm:ss');

    const realTime = DBO_TM.floor(liveTime!);
    return {
      content: aiContent,
      time: start_time
        ? getLiveTimeTitle({
            startTime: DBO_TM.subtract(startTime, realTime),
            endTime: DBO_TM.subtract(endTime, realTime),
          })
        : '',
    };
  }, [isEn, data]);

  useEffect(() => {
    setHideContentAnalysis?.(!content);
  }, [content]);

  return { content, time };
}
